import { Button } from '@invent/wl-ui-kit-next/ui';
import { CalendarClock, MapPin, Plus, Users } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DataTable, FilterBar, SelectChip, type IDataColumn } from '@/design';
import { useAdminData } from '@/domains/admin';
import { formatDateTime } from '@/domains/survey';
import type { IContentEvent } from '../api/event-contracts';
import { eventsApi } from '../api/events-api';
import type { ContentPostStatus } from '../api/post-contracts';
import { eventPath } from '../paths';
import { StatusPill, personName } from '../shared';
import { ContentPage, NEW_EVENT } from './content-page';

/**
 * The site's event pages — the list (WEB-08), over `/api/v1/admin/website/events`.
 *
 * **This lists; the editor edits.** An event's detail, a programme of a dozen sessions and a
 * speaker list are a screen of their own, for the reason a post has one: a programme that changes
 * weekly is somewhere an author stays.
 *
 * **The filter is the URL and survey-api applies it**, as every Team Portal list does — a
 * filtered view is pasteable, and a client-side filter is one that silently filters a page.
 * Archived is a filter rather than a second section, because the server already answers the live
 * first and the archived after them: a section would be this screen re-sorting what arrived
 * sorted.
 *
 * **"New event" creates it, then opens it.** A round trip rather than an empty form, for the
 * reason "Write a post" takes one: the address is the server's — derived from the title, refused
 * where another event holds it, frozen at publication — and a form that minted one here would be
 * guessing at all three.
 */

export function ContentEventsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [creating, setCreating] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const { data, error, reload } = useAdminData<IContentEvent[]>(
    (signal) => eventsApi.list(status ? (status as ContentPostStatus) : undefined, signal),
    [status]
  );

  const set = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete('status');
    else next.set('status', value);
    setParams(next, { replace: true });
  };

  const create = async () => {
    setCreating(true);
    setRefusal(null);
    try {
      const { body } = await eventsApi.create(NEW_EVENT());
      navigate(eventPath(body.eventId));
    } catch {
      // The placeholder address carries a token (`draftSlug`), so this is not a collision.
      setRefusal('The event was not created. Try again in a moment.');
      setCreating(false);
    }
  };

  const columns: IDataColumn<IContentEvent>[] = [
    {
      id: 'name',
      header: 'Event',
      cell: (happening) => (
        <div className="grid min-w-0 gap-0.5">
          <span className="truncate font-medium text-[var(--color-royal-blue)]">
            {happening.name}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            /events/{happening.slug}
          </span>
        </div>
      )
    },
    {
      id: 'when',
      header: 'When',
      cell: (happening) => (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
          {formatDateTime(happening.startsAt)}
        </span>
      )
    },
    {
      id: 'where',
      header: 'Where',
      cell: (happening) => {
        const place = [happening.venue, happening.city, happening.state]
          .filter(Boolean)
          .join(', ');
        return place ? (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden />
            {place}
          </span>
        ) : (
          <span className="text-muted-foreground">Not set</span>
        );
      }
    },
    {
      id: 'programme',
      header: 'Programme',
      cell: (happening) => (
        <span className="flex items-center gap-1 whitespace-nowrap text-muted-foreground">
          <Users className="h-3 w-3 shrink-0" aria-hidden />
          {happening.sessions.length} sessions · {happening.speakers.length} speaking
        </span>
      )
    },
    { id: 'status', header: 'Status', cell: (happening) => <StatusPill status={happening.status} /> },
    {
      id: 'updated',
      header: 'Last change',
      cell: (happening) => (
        <div className="grid gap-0.5">
          <span>{formatDateTime(happening.updatedAt)}</span>
          <span className="text-xs text-muted-foreground">
            by {personName(happening.updatedBy)}
          </span>
        </div>
      )
    }
  ];

  return (
    <ContentPage
      title="Events"
      lede="Event pages, their agendas and who is speaking."
      actions={
        <Button size="sm" onClick={create} disabled={creating}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {creating ? 'Creating…' : 'New event'}
        </Button>
      }
    >
      {refusal && <p className="text-sm text-[var(--color-deep-magenta)]">{refusal}</p>}

      <FilterBar>
        <SelectChip
          label="Status"
          any="Any status"
          value={status || null}
          onChoose={(value) => set(value ?? '')}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'published', label: 'On the site' },
            // AC4's archive: off the live listing, every word kept.
            { value: 'archived', label: 'Archived' }
          ]}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(happening) => happening.eventId}
        onOpen={(happening) => navigate(eventPath(happening.eventId))}
        label="Events"
        maxHeight="none"
        state={listState(data, error, reload, Boolean(status))}
      />
    </ContentPage>
  );
}

function listState(
  data: Nullable<IContentEvent[]>,
  error: Nullable<string>,
  reload: () => void,
  filtered: boolean
) {
  if (error) {
    return {
      kind: 'error' as const,
      content: (
        <div className="grid gap-2">
          <p>{error}</p>
          <div>
            <Button type="button" size="sm" variant="outline" onClick={reload}>
              Try again
            </Button>
          </div>
        </div>
      )
    };
  }
  if (!data) return { kind: 'loading' as const };
  return {
    kind: 'empty' as const,
    content: filtered
      ? 'No event has that status.'
      : 'No events yet. "New event" stands one up.'
  };
}
